import type { AgentTranscriptTurn } from '@travel-planner/shared/schemas';
import { create } from 'zustand';

// Conversation memory for the dynamic-agent loop (P2.5, #336). A bounded ring
// buffer of the recent turns, owned client-side and replayed on every
// `agentRunStream` call (mirrors how the page snapshot is ferried back each
// turn). The server caps + sanitises it and renders a "Recent conversation"
// prompt block so the model can resolve back-references ("add another like
// that"). NOTE: this is the dynamic-loop transcript — distinct from the
// scripted `commandSession` history in `use-agent-panel-store`.

// Keep in sync with `agentTranscriptSchema.max(6)` server-side: the client
// drops the oldest turn past this cap so the replayed buffer never grows.
export const MAX_TRANSCRIPT_TURNS = 6;

type AgentTranscriptState = {
  // Stable per-tab session id, minted lazily on the first recorded turn so the
  // server can key cross-turn undo (P2.6) to this conversation.
  sessionId: string | null;
  // The page context the current buffer belongs to. A run on a different
  // context starts a fresh conversation (see `prepareForContext`) so an
  // itinerary turn never bleeds into "add another like that" on products.
  contextType: string | null;
  turns: AgentTranscriptTurn[];
  // Returns the session id, minting one on first use. Called when building a
  // run input so every turn in a session shares the same id.
  ensureSessionId: () => string;
  // Scope the buffer to `contextType`: if it changed since the last run, reset
  // (new conversation) before minting/returning the session id. Returns the id.
  prepareForContext: (contextType: string) => string;
  // Append a finished turn, trimming to the most recent MAX_TRANSCRIPT_TURNS.
  recordTurn: (turn: AgentTranscriptTurn) => void;
  // Mark the most recent still-undoable turn as undone (clears its undoToken)
  // after a cross-turn "undo last change" so the control reflects the next one.
  markLatestUndone: () => void;
  // Clear the buffer + session (e.g. starting a fresh conversation).
  reset: () => void;
};

const mintId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const useAgentTranscriptStore = create<AgentTranscriptState>(
  (set, get) => ({
    sessionId: null,
    contextType: null,
    turns: [],
    ensureSessionId: () => {
      const existing = get().sessionId;
      if (existing) return existing;
      const id = mintId();
      set({ sessionId: id });
      return id;
    },
    prepareForContext: (contextType) => {
      const state = get();
      if (state.contextType !== null && state.contextType !== contextType) {
        // Switched pages — start a fresh conversation so back-references don't
        // cross contexts.
        const id = mintId();
        set({ sessionId: id, contextType, turns: [] });
        return id;
      }
      const id = state.sessionId ?? mintId();
      set({ sessionId: id, contextType });
      return id;
    },
    recordTurn: (turn) =>
      set((state) => ({
        turns: [...state.turns, turn].slice(-MAX_TRANSCRIPT_TURNS),
      })),
    markLatestUndone: () =>
      set((state) => {
        // Find the last turn that still carries an undo token and clear it,
        // mirroring the server consuming the most recent chain.
        let idx = -1;
        for (let i = state.turns.length - 1; i >= 0; i--) {
          if (state.turns[i]?.undoToken != null) {
            idx = i;
            break;
          }
        }
        if (idx === -1) return state;
        const turns = state.turns.map((t, i) =>
          i === idx ? { ...t, undoToken: null } : t
        );
        return { turns };
      }),
    reset: () => set({ sessionId: null, contextType: null, turns: [] }),
  })
);
